<template>
  <div class="section">
    <div class="heading-holder">
      <div class="heading-primary">{{ $t('my_account.addresses.delivery_address') }}</div>
      <a href="#" class="link" v-if="isLoggedIn" @click.prevent="openAddressModal('delivery_address')">{{ $t('my_account.addresses.add_address') }}</a>
    </div>
    <SavedAddresses v-if="isLoggedIn && enrichedAddresses.length" />
    <AddressForm
        v-else
        v-model="shippingAddress"
        :errors="errors.delivery_address"
        @leave="createNewAddress($event.address, 'delivery_address', $event.target, $event.shouldReplay)"
        @clearField="clearShippingError"
        @country-change="handleCountryChange"
        ref="shippingAddressForm"
        hideDefault
    />
    <div class="toggle-holder" v-if="!hideInvoiceAddress">
      <NBToggle :label="$t('shopping.basket.checkout.billing_delivery_same')" v-model="isBillingAddressSame" />
    </div>
  </div>
  <div class="section" v-show="!isBillingAddressSame && !hideInvoiceAddress">
    <div class="heading-holder">
      <div class="heading-primary">{{ $t('my_account.addresses.invoice_address') }}</div>
      <a href="#" class="link" v-if="isLoggedIn && enrichedAddresses.length" @click.prevent="openAddressModal('invoice_address')">{{ $t('my_account.addresses.add_address') }}</a>
    </div>
    <SavedAddresses v-if="isLoggedIn && enrichedAddresses.length" addressType="invoice_address" />
    <AddressForm
        v-else
        v-model="invoiceAddress"
        :allowStation="false"
        :errors="errors.invoice_address"
        @leave="createNewAddress($event.address, 'invoice_address', $event.target, $event.shouldReplay)"
        @clearField="clearInvoiceError"
        ref="invoiceAddressForm"
        hideDefault
    />
  </div>

  <BaseModal v-model="isModalOpen" class="address-modal">
    <template #header>
      <div class="modal-title">{{ $t('my_account.addresses.add_address') }}</div>
    </template>
    <AddressForm v-model="formAddress" @submit="createNewAddress($event, addressTypeForNew)" :showSave="true" :errors="errors[addressTypeForNew]" @clearField="clearErrorField" />
  </BaseModal>
</template>
<style lang="scss" src="./styles.scss" scoped></style>

<script setup>
import { nextTick } from 'vue'
import { useStore } from 'vuex'
import { showSnackbar } from '@/helper'
import { ref, computed, watch, reactive } from 'vue'
import { usePayment } from '@/composables/usePayment'
import BaseModal from '@/components/BaseModal/index.vue'
import { useAuth } from '@/app/Checkout/composables/useAuth'
import { useUser } from '@/app/Checkout/composables/useUser'
import { useCheckout } from '@/app/Checkout/composables/useCheckout'
import { useAddresses } from '@/app/Checkout/composables/useAddresses'
import NBToggle from '@/app/Checkout/components/common/NBToggle/index.vue'
import AddressForm from '@/app/Checkout/components/common/AddressForm/index.vue'
import { useAddressValidation } from '@/app/Checkout/composables/useAddressValidation'
import SavedAddresses from '@/app/Checkout/components/desktop/SavedAddresses/index.vue'
import { scrollToError } from '@/helper'

const store = useStore()
const isModalOpen = ref(false)
const { isLoggedIn } = useAuth()
const invoiceAddressForm = ref(null)
const shippingAddressForm = ref(null)
const shippingAddressErrors = ref({})
const { getPaymentMethods } = usePayment()
const differentInvoiceAddress = ref(false)
const { emptyAddress, emptyLoggedInNewAddress } = useAddressValidation()
const formAddress = ref(emptyAddress())
const addressTypeForNew = ref('delivery_address')
const { getAddresses, createAddress, addresses } = useUser()
const { errors, setErrors, resetErrors, clearFieldError, enrichedAddresses, isBillingAddressSame } = useAddresses()
const { isGermany, getCheckout, shippingAddress, invoiceAddress, saveAddresses, hideInvoiceAddress, setShippingAddress, setInvoiceAddress } = useCheckout()

const clearErrorField = (field) => {
  clearFieldError({ field, type: addressTypeForNew.value })
}

const clearShippingError = (field) => {
  clearFieldError({ field, type: 'delivery_address' })
}

const clearInvoiceError = (field) => {
  clearFieldError({ field, type: 'invoice_address' })
}

function openAddressModal(type = 'delivery_address') {
  addressTypeForNew.value = type
  formAddress.value = emptyLoggedInNewAddress()
  resetErrors(addressTypeForNew.value)

  isModalOpen.value = true
}

async function handleCountryChange() {
  const addressType = addressTypeForNew.value || 'delivery_address'
  const payload = shippingAddressForm.value?.formData?.()
  const extendedPayload = {
    ...payload,
    is_invoice: addressType === 'invoice_address',
    is_delivery: addressType === 'delivery_address',
    is_same: isBillingAddressSame.value,
  }

  try {
    const response = await createAddress(extendedPayload)

    if (response.redirect) {
      window.location.href = response.url
    } else {
      await getPaymentMethods()
      await getCheckout()
    }
  } catch (response) {
    if (response.redirect) {
      window.location.href = response.url
    } else {
      setErrors({ errors: response.errors, type: addressTypeForNew.value })
    }
  }
}

async function createNewAddress(data, type = addressTypeForNew.value, target = null, shouldReplay = true) {
  const addressType = type || addressTypeForNew.value || 'delivery_address'

  addressTypeForNew.value = addressType
  resetErrors(addressType)

  const extendedPayload = {
    ...data,
    is_invoice: addressType === 'invoice_address',
    is_delivery: addressType === 'delivery_address',
    is_same: isBillingAddressSame.value,
  }

  try {
    const response = await createAddress(extendedPayload)

    await getAddresses()

    const newId = response?.id ?? addresses.value?.[0]?.id
    if (newId) {
      const isDelivery = addressType === 'delivery_address'

      if (isDelivery) {
        await setShippingAddress(newId)

        if (!isBillingAddressSame.value) {
          await setInvoiceAddress(newId)
        }
      } else {
        await setInvoiceAddress(newId)
      }

      await getCheckout()
    }

    resetErrors(addressType)
    isModalOpen.value = false

    if (target && shouldReplay) {
      replayOriginalClick(target)
    }

    return response
  } catch (response) {
    setErrors({ errors: response.errors, type: addressType })
    await nextTick()
    scrollToError()
  }
}

function validateShippingAddressForm() {
  if (!shippingAddressForm.value) return true
  return shippingAddressForm.value.validateForm()
}

function validateInvoiceAddressForm() {
  if (!invoiceAddressForm.value) return true
  return invoiceAddressForm.value.validateForm(false)
}

function shippingFormData() {
  return shippingAddressForm.value?.formData?.() || {}
}

function invoiceFormData() {
  return invoiceAddressForm.value?.formData?.() || {}
}

defineExpose({
  invoiceFormData,
  shippingFormData,
  validateInvoiceAddressForm,
  validateShippingAddressForm,
})

function replayOriginalClick(target) {
  const clickable = target?.closest?.('a, button, input, label, select, option, [role="button"], [role="option"], [data-clickable]')

  if (clickable?.click) {
    clickable.click()
    return
  }

  target?.click?.()
}
</script>
